// layouts/DashboardLayout.tsx
import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider,
  useTheme, useMediaQuery, alpha, Tooltip, Badge, Chip, Collapse,
  Paper, Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  ManageSearch as ManageRecordsIcon,
  Assessment as ReportsIcon,
  PeopleAlt as ManageStaffsIcon,
  Logout as LogoutIcon,
  Email as EmailIcon,
  ExpandLess, ExpandMore,
  BarChart as DailySummaryIcon,
  ListAlt as RegisterReportIcon,
  Security as AuditLogIcon,
  NotificationsNone as NotificationsIcon,
  ChevronLeft as ChevronLeftIcon,
  Settings as SettingsIcon,
  Category as ProductCategoriesIcon,
  Inventory as ProductsIcon,
  Store as SellerInfoIcon,
  CreditCard as CreditCardIcon, 
  ShoppingCart as PurchaseIcon,
  Sell as SellIcon,
  KeyboardReturn as KeyboardReturnIcon,
  AttachMoney as MoneyIcon,
  AssignmentReturn as AssignmentReturnIcon, 
  Analytics as AnalyticsIcon,
  Inventory as InventoryIcon,
  VpnKey as VpnKeyIcon,
  Receipt as InvoiceIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png';

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 68;

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: { label: string; path: string; icon: React.ReactNode }[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard',     icon: <DashboardIcon />,      path: '/dashboard' },
  {
    label: 'Entry Point',
    icon: <AddIcon />,
    children: [
      { label: 'Purchase Info',   path: '/entry-form/purchase-info',    icon: <PurchaseIcon /> },
      { label: 'Selling Info', path: '/entry-form/selling-info', icon: <SellIcon /> },
      { label: 'Return Info', path: '/entry-form/return-info', icon: <KeyboardReturnIcon /> },
      { label: 'Cost Info', path: '/entry-form/cost-info', icon: <MoneyIcon /> },
      { label: 'ROI Info', path: '/entry-form/roi-info', icon: <AssignmentReturnIcon /> },
    ],
  },
  {
    label: 'Reports',
    icon: <ReportsIcon />,
    children: [
      { label: 'Stock Info',       path: '/reports/stock-info',    icon: <InventoryIcon /> },
      { label: 'Purchase Info',   path: '/reports/puchase-info',    icon: <PurchaseIcon /> },
      { label: 'Selling Info', path: '/reports/selling-info', icon: <SellIcon /> },
      { label: 'Return Info', path: '/reports/return-info', icon: <KeyboardReturnIcon /> },
      { label: 'Cost Info', path: '/reports/cost-info', icon: <MoneyIcon /> },
      { label: 'ROI Info', path: '/reports/roi-info', icon: <AssignmentReturnIcon /> },
      { label: 'Pivot Info', path: '/reports/pivot-info', icon: <AnalyticsIcon  /> },
      { label: 'Due Info', path: '/reports/due-info', icon: <CreditCardIcon /> },
      { label: 'Audit Info', path: '/reports/audit-info', icon: <AuditLogIcon /> },
    ],
  },
  { 
    label: 'Invoice Management', 
    icon: <InvoiceIcon />,    
    children: [
      { label: 'Invoice Info',   path: '/invoices/invoice-info',    icon: <InvoiceIcon /> },
    ],
  },
  { 
    label: 'Configuration', 
    icon: <SettingsIcon />,    
    children: [
      { label: 'Product Categories',   path: '/configuration/product-categories',    icon: <ProductCategoriesIcon /> },
      { label: 'Products',             path: '/configuration/products',              icon: <ProductsIcon /> },
      { label: 'Seller Information',   path: '/configuration/seller-information',    icon: <SellerInfoIcon /> },
      { label: 'Cost Categories',   path: '/configuration/cost-categories',    icon: <CreditCardIcon /> },
    ],
  },
  { label: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { label: 'Manage Staffs', icon: <ManageStaffsIcon />,    path: '/manage-staffs' },
];

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // FIXED: Check for /entry-form paths instead of /dashboard/entry-form
  const [entryPointOpen, setEntryPointOpen] = useState(
    location.pathname.startsWith('/entry-form')
  );
  const [reportsOpen, setReportsOpen] = useState(
    location.pathname.startsWith('/dashboard/reports')
  );
  const [configurationOpen, setConfigurationOpen] = useState(
    location.pathname.startsWith('/configuration')
  );
  const [invoiceOpen, setInvoiceOpen] = useState(
    location.pathname.startsWith('/invoices')
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const drawerWidth = collapsed && !isMobile ? DRAWER_COLLAPSED : DRAWER_WIDTH;
  const isActive = (path: string) => location.pathname === path;
  const isGroupActive = (paths: string[]) => paths.some(p => location.pathname.startsWith(p));

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login', { replace: true });
  };

  const getRoleColor = (roleName: string) => {
    const map: Record<string, string> = {
      DATA_ENTRY_OFFICER: '#2196F3',
      DATA_MANAGER: '#FF9800',
      LEAD_MANAGER: '#9C27B0',
    };
    return map[roleName] || '#667eea';
  };

  const getRoleLabel = (roleName: string) => {
    const map: Record<string, string> = {
      DATA_ENTRY_OFFICER: 'Entry Officer',
      DATA_MANAGER: 'Data Manager',
      LEAD_MANAGER: 'Lead Manager',
    };
    return map[roleName] || roleName;
  };

  const handleNavItemClick = (item: NavItem) => {
    if (item.children) {
      // For items with children, we just toggle the open state
      // FIXED: Check for "Entry Point" label
      if (item.label === 'Entry Point') {
        setEntryPointOpen(!entryPointOpen);
      } else if (item.label === 'Reports') {
        setReportsOpen(!reportsOpen);
      } else if (item.label === 'Configuration') {
        setConfigurationOpen(!configurationOpen);
      } else if (item.label === 'Invoice Management') {   
        setInvoiceOpen(!invoiceOpen);
      }
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // ── Drawer contents ────────────────────────────────────────────────────────
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Logo area */}
      <Box
        sx={{
          px: collapsed ? 1.5 : 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 64,
          borderBottom: `1px solid ${alpha('#667eea', 0.15)}`,
        }}
      >
        <Box sx={{ width: 38, height: 38, borderRadius: '10px', overflow: 'hidden', flexShrink: 0, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.35)' }}>
          <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
        {!collapsed && (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                whiteSpace: 'nowrap',
              }}
            >
              SP1 Global
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5 }}>
             Enterprise
            </Typography>
          </Box>
        )}
      </Box>

      {/* User profile mini card */}
      {!collapsed && (
        <Box
          sx={{
            mx: 1.5,
            mt: 2,
            mb: 1,
            p: 1.5,
            borderRadius: 2,
            background: alpha('#667eea', 0.06),
            border: `1px solid ${alpha('#667eea', 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontSize: '0.875rem',
                fontWeight: 700,
              }}
            >
              {user?.users_name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden', flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {user?.users_name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <EmailIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {user?.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Chip
            size="small"
            label={getRoleLabel(user?.role?.role_name || '')}
            icon={<AuditLogIcon style={{ fontSize: 12 }} />}
            sx={{
              mt: 1,
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 700,
              background: alpha(getRoleColor(user?.role?.role_name || ''), 0.12),
              color: getRoleColor(user?.role?.role_name || ''),
              border: `1px solid ${alpha(getRoleColor(user?.role?.role_name || ''), 0.25)}`,
              '& .MuiChip-icon': { color: getRoleColor(user?.role?.role_name || '') },
            }}
          />
        </Box>
      )}

      {/* Nav */}
      <List sx={{ flex: 1, px: collapsed ? 0.75 : 1.5, py: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {navItems.map((item) => {
          if (item.children) {
            const childPaths = item.children.map(c => c.path);
            const groupActive = isGroupActive(childPaths);
            
            // Determine which open state to use
            let isOpen = false;
            let toggleOpen = () => {};
            // FIXED: Check for "Entry Point" label
            if (item.label === 'Entry Point') {
              isOpen = entryPointOpen;
              toggleOpen = () => setEntryPointOpen(!entryPointOpen);
            } else if (item.label === 'Reports') {
              isOpen = reportsOpen;
              toggleOpen = () => setReportsOpen(!reportsOpen);
            } else if (item.label === 'Configuration') {
              isOpen = configurationOpen;
              toggleOpen = () => setConfigurationOpen(!configurationOpen);
            } else if (item.label === 'Invoice Management') {  
              isOpen = invoiceOpen;
              toggleOpen = () => setInvoiceOpen(!invoiceOpen);
            }
            
            return (
              <React.Fragment key={item.label}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip title={collapsed ? item.label : ''} placement="right">
                    <ListItemButton
                      onClick={() => !collapsed && toggleOpen()}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        px: collapsed ? 1.25 : 1.5,
                        background: groupActive ? alpha('#667eea', 0.1) : 'transparent',
                        color: groupActive ? '#667eea' : 'text.primary',
                        '&:hover': { background: alpha('#667eea', 0.08) },
                        justifyContent: collapsed ? 'center' : 'flex-start',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 'unset' : 36,
                          color: groupActive ? '#667eea' : 'text.secondary',
                          '& svg': { fontSize: 20 },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: groupActive ? 700 : 500 }}
                          />
                          {isOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
                {!collapsed && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ pl: 1.5 }}>
                      {item.children.map((child) => (
                        <ListItem key={child.path} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            onClick={() => navigate(child.path)}
                            sx={{
                              borderRadius: 2,
                              py: 0.75,
                              px: 1.5,
                              background: isActive(child.path) ? alpha('#667eea', 0.12) : 'transparent',
                              color: isActive(child.path) ? '#667eea' : 'text.secondary',
                              '&:hover': { background: alpha('#667eea', 0.06) },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '20%',
                                bottom: '20%',
                                width: 3,
                                borderRadius: 2,
                                background: isActive(child.path) ? '#667eea' : 'transparent',
                              },
                              position: 'relative',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: 'inherit', '& svg': { fontSize: 17 } }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: isActive(child.path) ? 700 : 400 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          }

          return (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={collapsed ? item.label : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate(item.path!)}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: collapsed ? 1.25 : 1.5,
                    background: isActive(item.path!) ? alpha('#667eea', 0.12) : 'transparent',
                    color: isActive(item.path!) ? '#667eea' : 'text.primary',
                    position: 'relative',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '15%',
                      bottom: '15%',
                      width: 3,
                      borderRadius: 2,
                      background: isActive(item.path!) ? '#667eea' : 'transparent',
                      transition: 'background 0.2s',
                    },
                    '&:hover': {
                      background: alpha('#667eea', 0.08),
                      '&::before': { background: alpha('#667eea', 0.5) },
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 'unset' : 36,
                      color: isActive(item.path!) ? '#667eea' : 'text.secondary',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isActive(item.path!) ? 700 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Logout */}
      <Divider sx={{ mx: 1.5, borderColor: alpha('#667eea', 0.1) }} />
      <Box sx={{ px: collapsed ? 0.75 : 1.5, py: 1.5 }}>
        <Tooltip title={collapsed ? 'Logout' : ''} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 1,
              px: collapsed ? 1.25 : 1.5,
              color: '#f44336',
              justifyContent: collapsed ? 'center' : 'flex-start',
              '&:hover': { background: alpha('#f44336', 0.08) },
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 'unset' : 36, color: '#f44336', '& svg': { fontSize: 20 } }}>
              <LogoutIcon />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              />
            )}
          </ListItemButton>
        </Tooltip>
        {!collapsed && (
        <>
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: 'InfoText', mt: 1, fontSize: '0.65rem' }}
          >
            © {new Date().getFullYear()} SP1 Global Enterprise
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: '#744577', mt: 0.5, fontSize: '0.7rem', fontWeight: 500 }}
          >
            Designed & Developed by{' '}
            <Link
              to="https://www.aimarttech.com/" // 👈 Replace with your actual route
              style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
              target="_blank"
            >
              AImart Tech
            </Link>
          </Typography>
        </>
      )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f4f6f9' }}>
      {/* ── Top AppBar ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha('#667eea', 0.1)}`,
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'text.secondary' }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* Desktop collapse toggle */}
          {!isMobile && (
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                color: 'text.secondary',
                width: 32,
                height: 32,
                transition: 'transform 0.3s ease',
                transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Notifications 
          <Tooltip title="Notifications">
            <IconButton sx={{ color: 'text.secondary' }}>
              <Badge badgeContent={0} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>  */}

          {/* User menu */}
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              ml: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              border: `1px solid ${alpha('#667eea', 0.15)}`,
              background: alpha('#667eea', 0.04),
              transition: 'all 0.2s ease',
              '&:hover': { background: alpha('#667eea', 0.08) },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontSize: '0.8rem',
                fontWeight: 700,
              }}
            >
              {user?.users_name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.0 }}>
                {user?.users_name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {getRoleLabel(user?.role?.role_name || '')}
              </Typography>
            </Box>
            <ExpandMore sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              elevation: 3,
              sx: { borderRadius: 3, minWidth: 200, mt: 1, border: `1px solid ${alpha('#667eea', 0.1)}` },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha('#000', 0.06)}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{user?.users_name}</Typography>
              <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            </Box>
            {/*<MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard/profile'); }}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              Profile & Settings
            </MenuItem>*/}
            <MenuItem 
              onClick={() => navigate('/change-password')}
              sx={{ 
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                mt: 1,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }
              }}
            >
              <ListItemIcon>
                <VpnKeyIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: '#f44336' }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#f44336' }} /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar Drawer ── */}
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            background: '#fff',
            borderRight: `1px solid ${alpha('#667eea', 0.1)}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            background: '#fff',
            borderRight: `1px solid ${alpha('#667eea', 0.1)}`,
            overflow: 'hidden',
            transition: 'width 0.3s ease',
            boxSizing: 'border-box',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          transition: 'margin 0.3s ease',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;